import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { useAuth } from '../../context/AuthContext';
import { Plus, Tag } from 'lucide-react';
import toast from 'react-hot-toast';

// Matches accounts-service's voucherTypesRoutes.js FINANCE_ROLES — only
// these roles can create a voucher type there, so the button is hidden for
// everyone else rather than showing a create form that will 403.
const FINANCE_ROLES = ['Super Admin', 'Secretary', 'Director'];

const emptyForm = { code: '', name: '', requires_payment_mode: true };

const VoucherTypes = () => {
  const { user } = useAuth();
  const isFinance = FINANCE_ROLES.includes(user?.role);

  const [voucherTypes, setVoucherTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchVoucherTypes = async () => {
    try {
      setLoading(true);
      const response = await accountsApi.get('/voucher-types');
      setVoucherTypes(response.data.voucher_types);
    } catch (err) {
      toast.error('Failed to load voucher types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVoucherTypes(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await accountsApi.post('/voucher-types', { ...form, code: form.code.trim().toUpperCase() });
      toast.success('Voucher type created');
      setShowCreateModal(false);
      setForm(emptyForm);
      fetchVoucherTypes();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create voucher type');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Voucher Types">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500 max-w-2xl">
          Categories vouchers are created under. <span className="font-medium text-gray-600">PAYMENT</span> and{' '}
          <span className="font-medium text-gray-600">RECEIPT</span> are special, built-in codes that auto-generate a
          cash/bank settlement line from the selected Payment Mode — any other code (including a new one you add here)
          takes fully explicit debit/credit lines entered by hand.
        </p>
        {isFinance && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900 shrink-0 ml-4"
          >
            <Plus size={16} /> New Voucher Type
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Requires Payment Mode</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : voucherTypes.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-8 text-gray-500">No voucher types yet</td></tr>
            ) : voucherTypes.map(t => (
              <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-700">{t.code}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{t.name}</td>
                <td className="px-4 py-3 text-center text-gray-600">{t.requires_payment_mode ? 'Yes' : 'No'}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2"><Tag size={18} /> New Voucher Type</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                <input required type="text" value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. WRITE-OFF"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                <p className="text-xs text-gray-400 mt-1">Saved in upper case. Must be unique.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input required type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Write-Off Voucher"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input id="requires_payment_mode" type="checkbox" checked={form.requires_payment_mode}
                  onChange={(e) => setForm({ ...form, requires_payment_mode: e.target.checked })}
                  className="rounded border-gray-300" />
                <label htmlFor="requires_payment_mode" className="text-sm text-gray-700">Requires a Payment Mode on the voucher form</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default VoucherTypes;
